import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect } from "react";
import { Sparkles, ArrowRight, TrendingUp, Users, Calendar } from "lucide-react";
import { adminDashboardSummary } from "@/lib/reservations.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: Dashboard,
});

function Dashboard() {
  const fn = useServerFn(adminDashboardSummary);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-summary"], queryFn: () => fn() });

  useEffect(() => {
    const ch = supabase
      .channel("admin-summary-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "reservations" },
        () => qc.invalidateQueries({ queryKey: ["admin-summary"] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  const cards = [
    { label: "Today's reservations", value: data?.todayCount ?? "—" },
    { label: "Expected covers", value: data?.covers ?? "—" },
    { label: "Pool bookings today", value: data?.poolToday ?? "—" },
    { label: "Pending approvals", value: data?.pendingApproval ?? "—" },
    { label: "Pending deposits", value: data?.pendingPayment ?? "—" },
    { label: "No-shows today", value: data?.noShows ?? "—" },
    { label: "Waitlist", value: data?.waitlistCount ?? "—" },
  ];

  return (
    <div>
      <h1 className="font-display text-3xl">Dashboard</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Operational overview · Europe/Dublin
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card/60 p-5">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</p>
            <p className="mt-2 font-display text-4xl text-primary">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-card/60 to-card/60 p-6">
        <Link to="/admin/ai" className="group flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-primary/15 p-2 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-xl">AI Manager — grand craic for your numbers</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Ask the AI for a quick summary of tonight's book, or a few sound tips to shift more pints and plates. Sure, it'll do the heavy lifting for ya.
              </p>
            </div>
          </div>
          <ArrowRight className="mt-2 h-5 w-5 text-primary opacity-60 transition group-hover:translate-x-1 group-hover:opacity-100" />
        </Link>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {[
            {
              icon: Calendar,
              label: "Tonight",
              prompt:
                "Give us the story on tonight's bookings — covers, pool tables, no-shows.",
            },
            {
              icon: TrendingUp,
              label: "Sales tips",
              prompt:
                "What's a good upsell for the trad session on Thursday? Any deadly deals I should run?",
            },
            {
              icon: Users,
              label: "Guests",
              prompt:
                "Any regulars I should be mindin' this week? Suggest a wee message to send them.",
            },
          ].map(({ icon: Icon, label, prompt }) => (
            <Link
              key={label}
              to="/admin/ai"
              search={{ q: prompt }}
              className="group block rounded-xl border border-border/60 bg-background/40 p-3 text-left transition hover:border-primary/60 hover:bg-background/70"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Icon className="h-3.5 w-3.5" /> {label}
              </div>
              <p className="mt-1 text-sm">"{prompt}"</p>
              <p className="mt-2 flex items-center gap-1 text-xs text-primary opacity-70 group-hover:opacity-100">
                Ask the AI <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
